import { SourceHTMLAttributes } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components/image/src/image";
import { wacToken } from "@/libraries/design/foundations";

export interface UniformImageV2Props {
    source: string;
    altText?: string;
    caption?: string;
    width?: string;
    maxWidth?: string;
    height?: string;
    maxHeight?: string;
    objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
    objectPosition?: string;
    tabletWidth?: string;
    desktopWidth?: string;
    tabletHeight?: string;
    desktopHeight?: string;
    makeCircle?: boolean;
    roundedCorners?: boolean;
    mobileSrc?: string;
    tabletSrc?: string;
    aspectRatio?: string;
}

const UniformImageV2 = ({
    source,
    altText = "",
    caption,
    width,
    maxWidth,
    height,
    maxHeight,
    objectFit,
    objectPosition,
    tabletWidth,
    desktopWidth,
    tabletHeight,
    desktopHeight,
    makeCircle = false,
    roundedCorners = false,
    mobileSrc,
    tabletSrc,
    aspectRatio
}: // eslint-disable-next-line complexity
UniformImageV2Props) => {
    if (!source) {
        return null;
    }

    const imageStyle = {
        maxWidth,
        maxHeight,
        objectFit,
        objectPosition,
        aspectRatio
    };

    const widthStyle = css({
        width: width ? width : "auto"
    });
    const heightStyle = css({
        height: height ? height : "auto"
    });

    const tabletWidthStyle = css({
        [media.above.sm]: {
            width: tabletWidth ? tabletWidth : "auto"
        }
    });
    const desktopWidthStyle = css({
        [media.above.md]: {
            width: desktopWidth ? desktopWidth : "auto"
        }
    });
    const tabletHeightStyle = css({
        [media.above.sm]: {
            height: tabletHeight ? tabletHeight : "auto"
        }
    });
    const desktopHeightStyle = css({
        [media.above.md]: {
            height: desktopHeight ? desktopHeight : "auto"
        }
    });

    // @ts-expect-error types
    const responsiveStyles = css([
        width && widthStyle,
        height && heightStyle,
        tabletWidth && tabletWidthStyle,
        desktopWidth && desktopWidthStyle,
        tabletHeight && tabletHeightStyle,
        desktopHeight && desktopHeightStyle
    ]);

    const makeCircleStyle = css({
        borderRadius: "50%"
    });

    const roundedCornersStyle = css({
        borderRadius: wacToken("corner.radius.card")
    });

    const pictureStyle = css({
        display: "block"
    });

    const { maxWidth: maxWidthStyle, ...restImageStyle } = imageStyle;

    const hasResponsiveSources = mobileSrc || tabletSrc;

    if (hasResponsiveSources) {
        const sources: Array<SourceHTMLAttributes<HTMLSourceElement>> = [];

        // The <source> media attribute requires a plain CSS media query string —
        // the @atlaskit/primitives media tokens (e.g. media.below.sm) are
        // Compiled-JS values and cannot be used here. The pixel values below
        // correspond to media.below.sm (< 768px = mobile) and
        // media.below.md (< 1024px = tablet), consistent with breakpoints used
        // elsewhere in this codebase.
        // Note: these use max-width (desktop-first) because <source> elements
        // are evaluated top-to-bottom by the browser — the most specific
        // (smallest) breakpoint must come first. This is separate from the
        // mobile-first min-width approach used for CSS sizing props above.
        if (mobileSrc) {
            sources.push({
                srcSet: mobileSrc,
                media: "(max-width: 767px)"
            });
        }

        if (tabletSrc) {
            sources.push({
                srcSet: tabletSrc,
                media: "(max-width: 1023px)"
            });
        }

        const pictureElement = (
            <picture
                css={[
                    responsiveStyles,
                    roundedCorners && roundedCornersStyle,
                    makeCircle && makeCircleStyle,
                    pictureStyle
                ]}
            >
                {sources.map((src, index) => (
                    <source key={index} {...src} />
                ))}
                {/* eslint-disable-next-line no-restricted-syntax -- art-directed <picture> element requires native <img> fallback; <Picture> component not suitable here as it makes its own assumptions */}
                <img
                    src={source}
                    alt={altText}
                    style={{
                        maxWidth: maxWidthStyle ?? "100%",
                        display: "block",
                        ...restImageStyle
                    }}
                />
            </picture>
        );

        if (caption) {
            return (
                <figure
                    css={css({
                        margin: 0
                    })}
                >
                    {pictureElement}
                    <figcaption
                        className="body-sm"
                        css={css({
                            marginBlockStart: token("space.100")
                        })}
                    >
                        {caption}
                    </figcaption>
                </figure>
            );
        }

        return pictureElement;
    }

    return (
        <Image
            src={source}
            alt={altText}
            caption={caption}
            style={imageStyle}
            css={[
                responsiveStyles,
                roundedCorners && roundedCornersStyle,
                makeCircle && makeCircleStyle
            ]}
        />
    );
};

export default UniformImageV2;
